import React, { Component, MouseEvent, ReactNode } from 'react';
import { RootState } from '../../redux/store';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import ComponentService from '../../service/ComponentService';
import ServiceContainer from '../../config/ServiceContainer';
import { ServiceNames } from '../../constant/ServiceNames';
import { changeLock, changeWorkers, ComponentWorker, leaveComponent } from '../../redux/actions/ComponentActions';
import Injectables from '../../constant/Injectables';
import { InstanceContainer } from '../../config/binding/InstanceBinding';
import { Socket } from 'socket.io-client';
import { FormicaProps, withFormica } from '../../helpers/withFormica';
import Icon from 'icons/Icon';
import { initialPagePath } from '../../redux/reducers/PageReducer';
import { customConfirm } from './CustomPopupConfirm';
import MessageUtil from '../../util/MessageUtil';
import LanguageUtil from 'util/LanguageUtil';

class ComponentWorkerComponent extends Component<Props> {
  private readonly componentService: ComponentService = ServiceContainer.getInstance().get(ServiceNames.COMPONENT);
  private readonly socketClient: Socket = InstanceContainer.get(Injectables.SOCKET_CLIENT);

  private colors: string[] = ['#728EFF', '#50A67F', '#F0A042', '#FF7020', '#027A48', '#ECFDF3', '#FCF9ED', '#EFF2FE'];

  public render(): ReactNode {
    const { component, user } = this.props;
    return (
      <div className='flex justify-end mr-4 gap-[10px]' data-testid='component_worker_container'>
        {component.workers.map((w: ComponentWorker, i: number) => (
          <span
            key={w.id + i.toString()}
            style={{ backgroundColor: this.colors[i % this.colors.length] }}
            className={`h-9 w-9 p-2 group flex relative justify-center items-center rounded-[50%] text-md
                ${component.lock?.id == w.id ? 'border border-green-300' : ''}`}
            data-testid='component_worker_item'
          >
            {w.avatarName.toUpperCase()}
            {w.id != this.socketClient.id && (user.user.role != 'Member' || user.user.roles?.includes('Admin')) && (
              <span
                className='invisible p-1 cursor-pointer group-hover:visible group-hover:bg-white rounded-[50%] absolute text-red-500'
                onClick={(e: MouseEvent<HTMLSpanElement>) => this.throwUser(e, w)}
              >
                <Icon icon='close' size={32} />
              </span>
            )}
          </span>
        ))}
      </div>
    );
  }

  public async componentDidMount() {
    const {
      component: { component, id },
    } = this.props;
    if (component != null && id != null) {
      this.initializeComponentListener();
      this.enterComponent();
    }
  }

  public componentWillUnmount() {
    this.leaveComponent();
  }

  private initializeComponentListener = () => {
    const {
      component: { component, id },
      changeWorkers,
      changeLock,
    } = this.props;
    this.componentService.listenEnter(component, id, (workers: ComponentWorker[]) => {
      changeWorkers(workers);
    });
    this.componentService.listenLeave(component, id, (workers: ComponentWorker[]) => {
      changeWorkers(workers);
    });
    this.componentService.listenLock(component, id, (worker: ComponentWorker | null) => {
      changeLock(worker);
    });
    this.componentService.listenThrow(component, id, (makerUsername: string) => {
      this.onThrowUser(makerUsername);
    });
  };

  private enterComponent = async () => {
    const {
      component: { component, id },
    } = this.props;
    if (component != null && id != null) {
      this.componentService.enter(component, id);
    }
  };

  private throwUser = (e: MouseEvent<HTMLSpanElement>, worker: ComponentWorker) => {
    const {
      component: { component, id },
    } = this.props;
    if (component != null && id != null && worker != undefined && worker.id != this.socketClient.id) {
      customConfirm({
        target: e.currentTarget,
        accept: () => this.componentService.throwUser(component, id, worker),
        message: LanguageUtil.get({ id: 'messages.confirm.confirmExpelUser', defaultMessage: 'Are you sure you want to expel user from this component ?' }),
      });
    }
  };

  private onThrowUser = (makerUsername: string) => {
    const {
      router: { navigate },
      component: { component },
    } = this.props;
    try {
      const path = initialPagePath[component];
      if (path && path.path != undefined) {
        navigate(path.path);
      } else {
        navigate('/');
      }
      MessageUtil.warning({
        summary: 'Expelled',
        detail: `You have been expelled from this component by ${makerUsername}`,
        life: 5000,
      });
    } catch (e) {}
  };

  private leaveComponent = () => {
    const {
      component: { component, id },
      changeWorkers,
    } = this.props;
    if (component != null && id != null) {
      this.componentService.leave(component, id);
      changeWorkers([]);
    }
  };
}

interface Props extends ReduxProps, FormicaProps {}

const mapStateToProps = (state: RootState) => ({
  component: state.component,
  user: state.auth,
});
const mapDispatchToProps = { changeWorkers, leaveComponent, changeLock };
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default withFormica(connector(ComponentWorkerComponent));
